import { CSSProperties, useCallback, useRef } from "react";
import { useParams } from "react-router";

import TableContextProvider from "components/Table/Context";
import Table from "components/Table";
import TableRow from "components/Table/Row";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import useTableSort from "components/Table/useTableSort";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import useTitle from "hooks/useTitle";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import NotFoundPage from "components/error/NotFoundPage";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import TableWrapper from "components/Table/Wrapper";
import useErrorHandle from "hooks/useErrorHandle";
import PageInfoHeader from "ds/components/PageInfoHeader";

import IdpGroupListItem from "./ListItem";
import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  USER_IDP_GROUPS_COLUMN_CONFIG,
  USER_IDP_GROUPS_COLUMNS_STORAGE_KEY,
  ROW_HEADER_ID,
  SORT_CONFIG,
} from "./constants";
import useGetUserGroups from "./useGetUserGroups";
import { IdpGroupWithMapped } from "./types";
import UserIdpGroupsEmpty from "./Empty";
import { useFilteredEntities } from "./useFilteredEntities";
import { FILTERS_ORDER_SETTINGS_KEY as USER_FILTERS_ORDER_SETTINGS_KEY } from "../../Users/ListNew/constants";

type UserIdpGroupsProps = {
  userName: string;
};

const UserIdpGroups = ({ userName }: UserIdpGroupsProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });
  const { userId } = useParams<{ userId: string }>();

  const { groups, loading, error, user } = useGetUserGroups({ id: userId });

  const filteredGroups = useFilteredEntities(groups || [], sortDescriptor);

  const isPageEmpty = filteredGroups.length === 0;

  useTitle(`Organization Settings · Users · ${userName} · IdP Groups`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "Users",
        link: getBreadcrumbsBackUrl("/settings/users", USER_FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: userName,
      },
    ],
    []
  );

  const renderRow = useCallback(
    (
      item: Partial<IdpGroupWithMapped> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <IdpGroupListItem group={item as IdpGroupWithMapped} />
        </TableRow>
      );
    },
    []
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!loading && !user) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="IdP groups">
        {!!groups?.length && (
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.GROUPS}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
          />
        )}
      </PageInfoHeader>
      <TableContextProvider
        columnsConfig={USER_IDP_GROUPS_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={USER_IDP_GROUPS_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        {!!filteredGroups?.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table
              loadingContent={loading}
              ariaLabel="IdP groups"
              items={filteredGroups}
              ref={virtualizedListContainerRef}
            >
              {renderRow}
            </Table>
          </TableWrapper>
        )}
        {!groups?.length && !loading && <UserIdpGroupsEmpty />}
        {isPageEmpty && !!groups?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableContextProvider>
    </>
  );
};

export default UserIdpGroups;
